"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AdminBar } from "./admin-bar";
import { AdminProvider } from "./admin-context";
import { AdminToastProvider } from "./admin-toast";
import { AuthRefresh } from "./auth-refresh";
import { TopBar } from "./top-bar";
import {
  DEFAULT_CHROME_STATE,
  type ChromeState,
  hideGlobalChrome,
  locationSlugFromPath,
} from "@/lib/nav/chrome-state";

export function ChromeShell({
  children,
  globalFooter,
}: {
  children: React.ReactNode;
  globalFooter: React.ReactNode;
}) {
  const pathname = usePathname() ?? "";
  const [chromeState, setChromeState] =
    useState<ChromeState>(DEFAULT_CHROME_STATE);

  // Monotonic request id shared by the pathname effect and the auth-change
  // listener: rapid navigation (or an auth refresh) bumps this, so a slower
  // earlier response that resolves late is retired instead of clobbering the
  // chrome for the route now on screen. The response body has no pathname to
  // compare against (and locationSlug has a cookie fallback), so the guard is
  // latest-write-wins, not field-equality.
  const requestIdRef = useRef(0);

  const loadChromeState = useCallback(async () => {
    const requestId = (requestIdRef.current += 1);
    try {
      const params = new URLSearchParams({ pathname });
      const response = await fetch(`/api/chrome-state?${params.toString()}`, {
        cache: "no-store",
      });
      if (!response.ok) return;
      const nextState = (await response.json()) as ChromeState;
      // Drop superseded responses: a newer navigation or auth refresh has
      // already started a later request, so this one must not win.
      if (requestId !== requestIdRef.current) return;
      setChromeState({ ...DEFAULT_CHROME_STATE, ...nextState });
    } catch (error) {
      console.error("[chrome-shell] failed to load chrome state", error);
    }
  }, [pathname]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- chrome state is external auth/session state fetched after hydration.
    void loadChromeState();
    return () => {
      // Retire the in-flight request so its late response cannot clobber the
      // next route's chrome.
      requestIdRef.current += 1;
    };
  }, [loadChromeState]);

  useEffect(() => {
    window.addEventListener("wildroost-auth-change", loadChromeState);
    return () => {
      window.removeEventListener("wildroost-auth-change", loadChromeState);
    };
  }, [loadChromeState]);

  const hideChrome = hideGlobalChrome(pathname);
  const isLocationPage = useMemo(
    () => locationSlugFromPath(pathname) !== null,
    [pathname],
  );

  return (
    <AdminProvider isAdmin={chromeState.isAdmin}>
      <AdminToastProvider>
        <div
          id="app-wrap"
          className={chromeState.isAdmin && !hideChrome ? "pt-7" : ""}
        >
          <AuthRefresh />
          {!hideChrome && <AdminBar />}
          {!hideChrome && (
            <>
              <TopBar state={chromeState} pathname={pathname} />
              <div className="h-16" aria-hidden="true" />
            </>
          )}
          {children}
          {!hideChrome && !isLocationPage ? globalFooter : null}
        </div>
      </AdminToastProvider>
    </AdminProvider>
  );
}
